import React, { useMemo, useState } from 'react';
import { ShieldAlertIcon, PlusIcon, SearchIcon, SaveIcon, SendIcon, Trash2Icon } from 'lucide-react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { BillingTaxTemplate, Invoice, InvoiceItem } from '../../types';
import { BillingSection } from './BillingModule';
import { buildInvoiceItem, calculateInvoicePreTaxLine, calculateInvoiceTotals, formatCurrency, INVOICE_TAX_RATE, recalculateInvoiceItem } from './utils';
type Props = {
  onNavigate: (section: BillingSection) => void;
};
const INVOICE_TAX_TEMPLATE: BillingTaxTemplate = {
  id: 'invoice-tot-tax-5',
  name: 'TOT Tax',
  type: 'Charge',
  rate: INVOICE_TAX_RATE,
  isActive: true
};
type EditableInvoiceField = 'serviceName' | 'description' | 'quantity' | 'unitPrice' | 'discount';
export function NewInvoice({
  onNavigate
}: Props) {
  const {
    patients,
    billingItems,
    billingSettings,
    addInvoice,
    currentUser,
    invoices,
    getPatientEncounters,
    logBillingAudit
  } = useData();
  const [customerType, setCustomerType] = useState<'Patient' | 'WalkIn'>('Patient');
  const [customerId, setCustomerId] = useState('');
  const [walkInName, setWalkInName] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [dueDate, setDueDate] = useState(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<InvoiceItem[]>([]);
  const [showItemSearch, setShowItemSearch] = useState(false);
  const [itemSearchQuery, setItemSearchQuery] = useState('');
  const activeBillingItems = billingItems.filter(item => item.status === 'Active');
  const filteredSearchItems = activeBillingItems.filter(item => item.serviceName.toLowerCase().includes(itemSearchQuery.toLowerCase()) || item.itemCode.toLowerCase().includes(itemSearchQuery.toLowerCase()));
  const nextInvoiceNumber = useMemo(() => {
    const prefix = billingSettings.invoicePrefix || 'INV-';
    const maxNumber = invoices.reduce((max, invoice) => {
      if (!invoice.invoiceNumber.startsWith(prefix)) return max;
      const value = Number.parseInt(invoice.invoiceNumber.slice(prefix.length), 10);
      return Number.isFinite(value) && value > max ? value : max;
    }, 0);
    return `${prefix}${String(maxNumber + 1).padStart(5, '0')}`;
  }, [billingSettings.invoicePrefix, invoices]);
  const totals = useMemo(() => calculateInvoiceTotals(items), [items]);
  if (!currentUser || currentUser.role !== 'Reception' && currentUser.role !== 'Administrator') {
    return <Card data-id="element-1426" data-mp="ag1fyod3u7u6">
        <div className="flex flex-col items-center gap-3 py-12 text-center" role="alert" data-id="element-1427" data-mp="afjcp7s0ytcr">
          <ShieldAlertIcon className="h-8 w-8 text-red-600" aria-hidden="true" data-id="element-1428" data-mp="kjzqzwpuw2eg" />
          <div data-id="element-1429" data-mp="fm6k11gqb4d4">
            <h2 className="text-lg font-semibold text-slate-900" data-id="element-1430" data-mp="fnr6e3sw3gzw">
              Billing access required
            </h2>
            <p className="mt-1 text-sm text-slate-600" data-id="element-1431" data-mp="6tcpjwspnyeo">
              Only Reception and Administrator users can create or edit
              invoices.
            </p>
          </div>
        </div>
      </Card>;
  }
  const addItem = (billingItem: (typeof billingItems)[number], description?: string) => {
    setItems(current => [...current, buildInvoiceItem(billingItem, INVOICE_TAX_TEMPLATE, {
      description
    })]);
    setShowItemSearch(false);
    setItemSearchQuery('');
  };
  const updateItem = (id: string, field: EditableInvoiceField, value: string | number) => {
    setItems(current => current.map(item => item.id === id ? recalculateInvoiceItem(updateInvoiceItemField(item, field, value)) : item));
  };
  const removeItem = (id: string) => {
    setItems(current => current.filter(item => item.id !== id));
  };
  const autoFillFromEncounter = () => {
    if (!customerId || customerType !== 'Patient') return;
    const encounter = getPatientEncounters(customerId).find(entry => entry.stage !== 'COMPLETED');
    if (!encounter) {
      window.alert('No active encounter found for this patient.');
      return;
    }
    const matches: Array<{
      item: (typeof billingItems)[number];
      description: string;
    }> = [];
    const consultation = billingItems.find(item => item.category === 'Consultation' && item.status === 'Active');
    if (consultation) matches.push({
      item: consultation,
      description: 'General Consultation'
    });
    encounter.labRequests.forEach(request => {
      request.tests.forEach(test => {
        const item = findMatchingBillingItem('Laboratory', test, billingItems);
        if (item) matches.push({
          item,
          description: `Lab Test: ${test}`
        });
      });
    });
    encounter.radiologyRequests.forEach(request => {
      const item = findMatchingBillingItem('Radiology', request.scanType, billingItems);
      if (item) matches.push({
        item,
        description: `Scan: ${request.scanType}`
      });
    });
    encounter.prescriptions.forEach(prescription => {
      const item = findMatchingBillingItem('Pharmacy', prescription.drugName, billingItems);
      if (item) matches.push({
        item,
        description: `Prescription: ${prescription.drugName}`
      });
    });
    if (!matches.length) {
      window.alert('No matching billable services were found for this encounter.');
      return;
    }
    const newItems = matches.map(({
      item,
      description
    }) => buildInvoiceItem(item, INVOICE_TAX_TEMPLATE, {
      description
    }));
    setItems(current => [...current, ...newItems]);
  };
  const saveInvoice = (status: 'Draft' | 'Sent') => {
    const typedWalkInName = walkInName.trim();
    if (customerType === 'Patient' && !customerId) {
      window.alert('Please select a registered patient.');
      return;
    }
    if (customerType === 'WalkIn' && !typedWalkInName) {
      window.alert('Please enter the walk-in client’s name.');
      return;
    }
    if (!items.length) {
      window.alert('Please add at least one service to the invoice.');
      return;
    }
    const recalculatedItems = items.map(recalculateInvoiceItem);
    const recalculatedTotals = calculateInvoiceTotals(recalculatedItems);
    const payload: Omit<Invoice, 'id'> = {
      invoiceNumber: nextInvoiceNumber,
      customerId: customerType === 'WalkIn' ? `walkin-manual-${Date.now()}` : customerId,
      customerName: customerType === 'WalkIn' ? typedWalkInName : undefined,
      customerType,
      date,
      dueDate,
      salesperson: currentUser.id,
      notes: notes.trim() || undefined,
      paymentTerms: billingSettings.paymentTerms,
      status,
      items: recalculatedItems,
      subtotal: recalculatedTotals.subtotal,
      totalDiscount: recalculatedTotals.totalDiscount,
      totalTax: recalculatedTotals.totalTax,
      totalAmount: recalculatedTotals.totalAmount,
      amountPaid: 0,
      balanceDue: recalculatedTotals.totalAmount,
      createdAt: new Date().toISOString(),
      createdBy: currentUser.id
    };
    addInvoice(payload);
    logBillingAudit('Create Invoice', `Created invoice ${payload.invoiceNumber} for ${formatCurrency(payload.totalAmount)}`);
    onNavigate(status === 'Draft' ? 'invoices-draft' : 'invoices-sent');
  };
  return <div className="mx-auto max-w-6xl space-y-6 pb-24" data-id="element-1432" data-mp="1nuarb726wjoe">
      <div className="flex flex-col gap-3 border-b border-slate-200 pb-5 sm:flex-row sm:items-end sm:justify-between" data-id="element-1433" data-mp="1nspvle452uoy">
        <div data-id="element-1434" data-mp="3nyn0a4899po7">
          <p className="text-sm font-semibold text-pink-600" data-id="element-1435" data-mp="3ry5igs8ozmnc">
            Billing workspace
          </p>
          <h2 className="mt-1 text-2xl font-bold tracking-tight text-slate-950" data-id="element-1436" data-mp="3dymbw3vj4jd5">
            New Invoice
          </h2>
          <p className="mt-1 text-sm text-slate-600" data-id="element-1437" data-mp="3iwnuknp1mgo3">
            A single 5% TOT tax is calculated on the discounted invoice amount.
          </p>
        </div>
        <span className="w-fit rounded-md border border-slate-200 bg-white px-3 py-1.5 font-mono text-sm font-semibold text-slate-700" data-id="element-1438" data-mp="ak03jp79693a">
          {nextInvoiceNumber}
        </span>
      </div>

      <Card data-id="element-1439" data-mp="pqas6eef8s1">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2" data-id="element-1440" data-mp="5qxk49w6wg0t">
          <div className="space-y-4" data-id="element-1441" data-mp="5pcla82ueax5">
            <Select label="Customer Type" value={customerType} onChange={event => {
            setCustomerType(event.target.value as 'Patient' | 'WalkIn');
            setCustomerId('');
            setWalkInName('');
          }} options={[{
            value: 'Patient',
            label: 'Registered Patient'
          }, {
            value: 'WalkIn',
            label: 'Walk-in Client'
          }]} data-id="element-1442" data-mp="tgi0cl0ykz6e" />
            {customerType === 'Patient' ? <Select label="Select Patient" value={customerId} onChange={event => setCustomerId(event.target.value)} options={[{
            value: '',
            label: '-- Select Patient --'
          }, ...patients.map(patient => ({
            value: patient.id,
            label: `${patient.bmcFileNumber} — ${patient.firstName} ${patient.lastName}`
          }))]} data-id="element-1443" data-mp="1zxzqjxbsw273" /> : <Input label="Walk-in Client Name" required value={walkInName} onChange={event => setWalkInName(event.target.value)} placeholder="Enter the client’s full name" data-id="element-1444" data-mp="6x502mf249yp" />}
          </div>
          <div className="space-y-4" data-id="element-1445" data-mp="3pbnrub6s47n1">
            <Input label="Invoice Date" type="date" value={date} onChange={event => setDate(event.target.value)} data-id="element-1446" data-mp="3pam9rmrcikvt" />
            <Input label="Due Date" type="date" value={dueDate} onChange={event => setDueDate(event.target.value)} data-id="element-1447" data-mp="3fc6j1kw8ddmu" />
          </div>
        </div>
      </Card>

      <Card noPadding data-id="element-1448" data-mp="36ecqml1l744y">
        <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-1449" data-mp="36ew67v3rlgft">
          <div data-id="element-1450" data-mp="14m82zlr4et01">
            <h3 className="font-bold text-slate-900" data-id="element-1451" data-mp="14mhqn2op0o2k">Invoice services</h3>
            <p className="mt-0.5 text-sm text-slate-500" data-id="element-1452" data-mp="2mjl622wln9t7">
              Line totals are after discount and before the invoice-level 5% TOT
              tax.
            </p>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-1453" data-mp="2w834idhep4to">
            {customerType === 'Patient' && customerId && <Button size="sm" variant="outline" onClick={autoFillFromEncounter} data-id="element-1454" data-mp="30ag8oxgi8uoc">
                Auto-fill visit charges
              </Button>}
            <Button size="sm" onClick={() => setShowItemSearch(true)} data-id="element-1455" data-mp="28jtppvim1hc1">
              <PlusIcon className="mr-1.5 h-4 w-4" data-id="element-1456" data-mp="2ckcgdm8rohcn" /> Add service
            </Button>
          </div>
        </div>

        {showItemSearch && <div className="border-b border-blue-100 bg-blue-50 px-5 py-4" data-id="element-1457" data-mp="2hl033vpyqc12">
            <label className="sr-only" htmlFor="invoice-service-search" data-id="element-1458" data-mp="3nyzhrf7dpbhh">
              Search service catalogue
            </label>
            <div className="relative max-w-xl" data-id="element-1459" data-mp="3t0yjylrdu2cu">
              <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" data-id="element-1460" data-mp="1svupj6lfof0h" />
              <input id="invoice-service-search" autoFocus value={itemSearchQuery} onChange={event => setItemSearchQuery(event.target.value)} placeholder="Search the service catalogue..." className="w-full rounded-md border border-blue-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200" data-id="element-1461" data-mp="22vbsvhd7qgta" />
            </div>
            {itemSearchQuery && <ul className="mt-2 max-h-56 max-w-xl overflow-y-auto rounded-md border border-slate-200 bg-white shadow-sm" aria-label="Matching services" data-id="element-1462" data-mp="1f4ftnz6fgjc0">
                {filteredSearchItems.length ? filteredSearchItems.map(item => <li key={item.id} data-id="element-1463" data-mp="2y2cfqhizwyl7">
                      <button type="button" onClick={() => addItem(item)} className="flex w-full items-center justify-between gap-4 border-b border-slate-100 px-3 py-2.5 text-left last:border-0 hover:bg-slate-50 focus:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500" data-id="element-1464" data-mp="32um07i1cd6g3">
                        <span data-id="element-1465" data-mp="3lthuetd79ktx">
                          <span className="block text-sm font-semibold text-slate-900" data-id="element-1466" data-mp="3ltq7omd8is5n">
                            {item.serviceName}
                          </span>
                          <span className="block text-xs text-slate-500" data-id="element-1467" data-mp="3css2i0r28elh">
                            {item.itemCode} · {item.category}
                          </span>
                        </span>
                        <span className="shrink-0 text-sm font-semibold text-slate-700" data-id="element-1468" data-mp="21dmwonvmtzxn">
                          {formatCurrency(item.unitPrice)}
                        </span>
                      </button>
                    </li>) : <li className="px-3 py-4 text-center text-sm text-slate-500" data-id="element-1469" data-mp="2bv8m5479hk10">
                    No matching services found.
                  </li>}
              </ul>}
            <button type="button" onClick={() => {
          setShowItemSearch(false);
          setItemSearchQuery('');
        }} className="mt-3 text-sm font-semibold text-blue-700 hover:text-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-1470" data-mp="22xpvnkaumdhq">
              Close search
            </button>
          </div>}

        <div className="overflow-x-auto" data-id="element-1471" data-mp="2loh1wczjhnrw">
          <table className="min-w-[760px] w-full divide-y divide-slate-200" data-id="element-1472" data-mp="1f6tye7nmiq33">
            <thead className="bg-slate-50" data-id="element-1473" data-mp="1a77j7so8izrn">
              <tr data-id="element-1474" data-mp="1a66b9x2imte5">
                <th scope="col" className="w-[42%] px-5 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500" data-id="element-1475" data-mp="1a5wg7v2m5w1l">
                  Service
                </th>
                <th scope="col" className="w-[20%] px-4 py-3 text-right text-xs font-bold uppercase tracking-wide text-slate-500" data-id="element-1476" data-mp="3uv98zvmfmi69">
                  Unit Price
                </th>
                <th scope="col" className="w-[18%] px-4 py-3 text-right text-xs font-bold uppercase tracking-wide text-slate-500" data-id="element-1477" data-mp="8gg2a4oxwjqx">
                  Discount
                </th>
                <th scope="col" className="w-[20%] px-5 py-3 text-right text-xs font-bold uppercase tracking-wide text-slate-500" data-id="element-1478" data-mp="iecfr35cnqs7">
                  Pre-tax Total
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white" data-id="element-1479" data-mp="2o7u5c1fsfkyr">
              {items.length ? items.map(item => {
              const line = calculateInvoicePreTaxLine(item);
              return <tr key={item.id} data-id="element-1480" data-mp="3mkq11pndw5ns">
                      <td className="px-5 py-4 align-top" data-id="element-1481" data-mp="4didmhyrus3k">
                        <div className="flex items-start gap-2" data-id="element-1482" data-mp="4aw6gjfsmelv">
                          <div className="min-w-0 flex-1" data-id="element-1483" data-mp="acedua2u16l">
                            <label className="sr-only" htmlFor={`service-${item.id}`} data-id="element-1484" data-mp="eawugiolxiwe">
                              Service name
                            </label>
                            <input id={`service-${item.id}`} value={item.serviceName} onChange={event => updateItem(item.id, 'serviceName', event.target.value)} className="w-full border-0 border-b border-transparent bg-transparent px-0 py-0.5 text-sm font-semibold text-slate-900 outline-none hover:border-slate-300 focus:border-blue-500 focus:ring-0" data-id="element-1485" data-mp="o0ggfx7m9svh" />
                            <label className="sr-only" htmlFor={`description-${item.id}`} data-id="element-1486" data-mp="16nguqv7jvc5f">
                              Service description
                            </label>
                            <input id={`description-${item.id}`} value={item.description || ''} placeholder="Description (optional)" onChange={event => updateItem(item.id, 'description', event.target.value)} className="mt-1 w-full border-0 bg-transparent px-0 py-0 text-xs text-slate-500 outline-none placeholder:text-slate-400 focus:ring-0" data-id="element-1487" data-mp="j0h68g9d7sjv" />
                            <div className="mt-2 flex items-center gap-2" data-id="element-1488" data-mp="gft40eb8stz3">
                              <label htmlFor={`quantity-${item.id}`} className="text-xs font-medium text-slate-500" data-id="element-1489" data-mp="bib57gqxvv4t">
                                Qty
                              </label>
                              <input id={`quantity-${item.id}`} aria-label={`${item.serviceName} quantity`} type="number" min="1" step="1" value={item.quantity} onChange={event => updateItem(item.id, 'quantity', Math.max(1, Number(event.target.value) || 1))} className="w-16 rounded border border-slate-300 px-2 py-1 text-right text-xs text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" data-id="element-1490" data-mp="3sy9fv41ge8i3" />
                            </div>
                          </div>
                          <button type="button" onClick={() => removeItem(item.id)} aria-label={`Remove ${item.serviceName}`} className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-500" data-id="element-1491" data-mp="x137ivjoqyll">
                            <Trash2Icon className="h-4 w-4" data-id="element-1492" data-mp="ied83achaxla" />
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-4 align-top" data-id="element-1493" data-mp="nd6kt0jxykke">
                        <label className="sr-only" htmlFor={`price-${item.id}`} data-id="element-1494" data-mp="3lwd8a3bslyoc">
                          Unit price for {item.serviceName}
                        </label>
                        <input id={`price-${item.id}`} type="number" min="0" step="0.01" value={item.unitPrice} onChange={event => updateItem(item.id, 'unitPrice', Math.max(0, Number(event.target.value) || 0))} className="w-full rounded border border-slate-300 px-2 py-1.5 text-right text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" data-id="element-1495" data-mp="3vtrktruli3bd" />
                        <p className="mt-1 text-right text-xs text-slate-500" data-id="element-1496" data-mp="2ln5ggxjll13l">
                          Line {formatCurrency(line.subtotal)}
                        </p>
                      </td>
                      <td className="px-4 py-4 align-top" data-id="element-1497" data-mp="2ctikb6bh9vtv">
                        <label className="sr-only" htmlFor={`discount-${item.id}`} data-id="element-1498" data-mp="27v7d73ckhzfc">
                          Discount percentage for {item.serviceName}
                        </label>
                        <div className="relative" data-id="element-1499" data-mp="1xznkeptavgut">
                          <input id={`discount-${item.id}`} type="number" min="0" max="100" step="0.01" disabled={item.discountEligible === false} value={item.discount} onChange={event => updateItem(item.id, 'discount', Math.min(100, Math.max(0, Number(event.target.value) || 0)))} className="w-full rounded border border-slate-300 px-2 py-1.5 pr-6 text-right text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-100" data-id="element-1500" data-mp="1txaf22igtkyk" />
                          <span className="pointer-events-none absolute right-2 top-1.5 text-xs text-slate-500" data-id="element-1501" data-mp="302zetjwyh1pb">
                            %
                          </span>
                        </div>
                        <p className="mt-1 text-right text-xs text-red-600" data-id="element-1502" data-mp="39tu5r1l5cph3">
                          -{formatCurrency(line.discountAmount)}
                        </p>
                      </td>
                      <td className="px-5 py-4 text-right align-top" data-id="element-1503" data-mp="2h43ncgchf1f6">
                        <p className="text-sm font-bold text-slate-950" data-id="element-1504" data-mp="2h6gbxccqwa17">
                          {formatCurrency(line.total)}
                        </p>
                        <p className="mt-1 text-xs text-slate-500" data-id="element-1505" data-mp="2r3m2mjhg5z0w">
                          5% TOT tax added once below
                        </p>
                      </td>
                    </tr>;
            }) : <tr data-id="element-1506" data-mp="1dr6nk83o94d">
                  <td colSpan={4} className="px-5 py-12 text-center" data-id="element-1507" data-mp="1eado5w7pd3m">
                    <p className="text-sm font-semibold text-slate-700" data-id="element-1508" data-mp="271rko3fi9bun">
                      No services added
                    </p>
                    <p className="mt-1 text-sm text-slate-500" data-id="element-1509" data-mp="2c1xffsh2negy">
                      Use “Add service” to build this invoice.
                    </p>
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>

        <div className="grid gap-6 border-t border-slate-200 bg-slate-50 p-5 lg:grid-cols-[minmax(0,1fr)_320px]" data-id="element-1510" data-mp="2q0ncx97szg5u">
          <div data-id="element-1511" data-mp="2uzgo2uzmav2u">
            <label htmlFor="invoice-notes" className="block text-sm font-semibold text-slate-700" data-id="element-1512" data-mp="2uyyl7fig9ily">
              Customer notes
            </label>
            <textarea id="invoice-notes" rows={4} value={notes} onChange={event => setNotes(event.target.value)} placeholder="Notes, terms, or payment instructions..." className="mt-2 w-full rounded-md border border-slate-300 bg-white p-3 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100" data-id="element-1513" data-mp="34q1lwg7gvvkp" />
          </div>
          <dl className="space-y-3 rounded-lg border border-slate-200 bg-white p-4 text-sm shadow-sm" data-id="element-1514" data-mp="t26jqryomtiz">
            <div className="flex justify-between gap-4" data-id="element-1515" data-mp="o33f38mm6pbj">
              <dt className="text-slate-600" data-id="element-1516" data-mp="o4o9my8xi89e">Subtotal</dt>
              <dd className="font-medium text-slate-900" data-id="element-1517" data-mp="1ktvvk7dgmjdu">
                {formatCurrency(totals.subtotal)}
              </dd>
            </div>
            {totals.totalDiscount > 0 && <div className="flex justify-between gap-4" data-id="element-1518" data-mp="1c0sl87y42mnf">
                <dt className="text-slate-600" data-id="element-1519" data-mp="1bz7s8mrg3bro">Discount</dt>
                <dd className="font-medium text-red-600" data-id="element-1520" data-mp="172r1dksv2jaw">
                  -{formatCurrency(totals.totalDiscount)}
                </dd>
              </div>}
            <div className="flex justify-between gap-4" data-id="element-1521" data-mp="1zqmz8su62h99">
              <dt className="text-slate-600" data-id="element-1522" data-mp="1zp23x1012uac">TOT Tax (5%)</dt>
              <dd className="font-medium text-slate-900" data-id="element-1523" data-mp="2imdfnxh34ur9">
                {formatCurrency(totals.totalTax)}
              </dd>
            </div>
            <div className="flex justify-between gap-4 border-t border-slate-200 pt-3 text-base font-bold" data-id="element-1524" data-mp="2nl6r7kctabos">
              <dt className="text-slate-950" data-id="element-1525" data-mp="29sh5zi53xasq">Total</dt>
              <dd className="text-blue-700" data-id="element-1526" data-mp="2dvdjwvjpciqc">
                {formatCurrency(totals.totalAmount)}
              </dd>
            </div>
          </dl>
        </div>
      </Card>

      <div className="fixed bottom-0 left-0 right-0 z-10 border-t border-slate-200 bg-white p-4 shadow-[0_-4px_10px_rgba(15,23,42,0.06)] md:left-64" data-id="element-1527" data-mp="24v78df7kdstt">
        <div className="mx-auto flex max-w-6xl justify-end gap-3" data-id="element-1528" data-mp="1qxaa8edgped1">
          <Button variant="outline" onClick={() => saveInvoice('Draft')} data-id="element-1529" data-mp="1uwj2tfwrn670">
            <SaveIcon className="mr-1.5 h-4 w-4" data-id="element-1530" data-mp="3la02kro6mfib" /> Save draft
          </Button>
          <Button onClick={() => saveInvoice('Sent')} data-id="element-1531" data-mp="3l7w0mf9ufqko">
            <SendIcon className="mr-1.5 h-4 w-4" data-id="element-1532" data-mp="3paagqrm4oc89" /> Save &amp; send
          </Button>
        </div>
      </div>
    </div>;
}
function updateInvoiceItemField(item: InvoiceItem, field: EditableInvoiceField, value: string | number): InvoiceItem {
  switch (field) {
    case 'serviceName':
      return {
        ...item,
        serviceName: String(value)
      };
    case 'description':
      return {
        ...item,
        description: String(value)
      };
    case 'quantity':
      return {
        ...item,
        quantity: Number(value)
      };
    case 'unitPrice':
      return {
        ...item,
        unitPrice: Number(value)
      };
    case 'discount':
      return {
        ...item,
        discount: Number(value)
      };
  }
}
function findMatchingBillingItem(category: string, service: string, billingItems: ReturnType<typeof useData>['billingItems']) {
  return billingItems.find(item => item.status === 'Active' && item.category === category && (item.serviceName.toLowerCase().includes(service.toLowerCase()) || service.toLowerCase().includes(item.serviceName.toLowerCase())));
}